import { useEffect } from "react";
import FR from "@/i18n/fr.json";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CartDrawer } from "@/components/CartDrawer";

const SITE = "https://dirtypawsapothecary.com";

const setLink = (rel, href, hreflang) => {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(sel);
  if (!el) { el = document.createElement("link"); el.rel = rel; if (hreflang) el.hreflang = hreflang; document.head.appendChild(el); }
  el.href = href;
};

const setPageLinks = (path, noindex) => {
  const fr = path === "/fr" || path.startsWith("/fr/");
  const base = fr ? path.slice(3) || "/" : path;
  setLink("canonical", `${SITE}${path}`);
  setLink("alternate", `${SITE}${base}`, "en-CA");
  setLink("alternate", `${SITE}/fr${base === "/" ? "" : base}`, "fr-CA");
  setLink("alternate", `${SITE}${base}`, "x-default");
  let robots = document.head.querySelector('meta[name="robots"]');
  if (!robots) { robots = document.createElement("meta"); robots.name = "robots"; document.head.appendChild(robots); }
  robots.content = noindex ? "noindex, follow" : "index, follow";
};

export const usePageMeta = (title, description, noindex = false) => {
  const path = window.location.pathname;
  useEffect(() => {
    const fr = path === "/fr" || path.startsWith("/fr/");
    const tr = (s) => (fr && FR[s]) || s;
    document.title = tr(title);
    document.querySelector('meta[name="description"]')?.setAttribute("content", tr(description));
    setPageLinks(path, noindex);
  }, [title, description, noindex, path]);
};

export const useJsonLd = (id, data) => {
  const text = data ? JSON.stringify(data) : "";
  useEffect(() => {
    if (!text) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    el.text = text;
    document.getElementById(id)?.remove();
    document.head.appendChild(el);
    return () => el.remove();
  }, [id, text]);
};

export const productLd = (p) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: p.name,
  description: `${p.lead} ${p.leadRest}`,
  image: `${SITE}${p.image}`,
  brand: { "@type": "Brand", name: "Dirty Paws Apothecary" },
  offers: { "@type": "Offer", price: (p.priceCents / 100).toFixed(2), priceCurrency: "CAD", availability: "https://schema.org/PreOrder", url: `${SITE}/products/${p.slug}` },
});

export const PageShell = ({ children, testid, page = "home", closing = false }) => (
  <div className={`kit k-${page}`} data-testid={testid}>
    <Header />
    <main>{children}</main>
    <Footer closing={closing} />
    <CartDrawer />
  </div>
);

/* Pages whose body still uses the older utility classes: same kit header and footer around them.
   Sets canonical/hreflang/robots; a page's own usePageMeta (which runs after) takes precedence. */
export const LegacyShell = ({ children, testid, noindex = false }) => {
  const path = window.location.pathname;
  useEffect(() => { setPageLinks(path, noindex); }, [path, noindex]);
  return (
    <div className="min-h-screen bg-[#0E0F10]" data-testid={testid}>
      <div className="kit"><Header /></div>
      {children}
      <div className="kit"><Footer /><CartDrawer /></div>
    </div>
  );
};
